import ArrowDropDownIcon from "@assets/icons/new/arrow-down_21px.svg?react";
import { FaroTextButton } from "@components/common/faro-text-button";
import { blue, Text } from "@flat-ui";
import {
  Box,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  SvgIcon,
} from "@mui/material";
import { getSvgColoredIconCss } from "@utils/ui-utils";
import { useRef, useState } from "react";

interface DropdownAction {
  /** The label to display for the dropdown item. */
  label: string;

  /** The function to execute when the item is clicked. */
  onClick: () => void;
}

interface Props {
  /** The main button label shown next to the status indicator and dropdown icon. */
  label: string;

  /**
   * Optional color for the circular status indicator shown on the left of the label.
   * If undefined, no indicator is shown.
   */
  statusChipColor?: string;

  /** List of actions to be rendered in the dropdown menu. */
  actions: DropdownAction[];
}

/**
 * DropdownButton renders a customizable button with a dropdown menu.
 *
 * It optionally displays a colored status indicator (a small circle) to the left of the label.
 * When clicked, it toggles a dropdown menu with the provided action items.
 */
export function DropdownButton({
  label,
  statusChipColor,
  actions,
}: Props): JSX.Element {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpenPopover, setIsOpenPopover] = useState(false);

  function handleMenuItemClick(action: DropdownAction): void {
    setIsOpenPopover(false);
    action.onClick();
  }

  return (
    <Box ref={anchorRef}>
      <FaroTextButton
        onClick={() => setIsOpenPopover(!isOpenPopover)}
        sx={{ display: "flex", gap: "0.5rem", padding: "0.5rem 1rem" }}
      >
        {statusChipColor && (
          <Box
            sx={{
              height: "0.75rem",
              width: "0.75rem",
              background: statusChipColor,
              borderRadius: "50%",
            }}
          />
        )}

        {label}
        <SvgIcon
          component={ArrowDropDownIcon}
          sx={{
            transform: isOpenPopover ? "rotate(-180deg)" : "none",
            transition: "transform 0.3s",
            ...getSvgColoredIconCss(blue[500]),
          }}
        />
      </FaroTextButton>

      <Popper
        sx={{ zIndex: 1 }}
        open={isOpenPopover}
        anchorEl={anchorRef.current}
        transition
        disablePortal
      >
        {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper sx={{ width: anchorRef.current?.offsetWidth ?? "auto" }}>
              <ClickAwayListener onClickAway={() => setIsOpenPopover(false)}>
                <MenuList autoFocusItem>
                  {actions.map((action, idx) => (
                    <MenuItem
                      key={idx}
                      onClick={() => handleMenuItemClick(action)}
                    >
                      <Text variant="bodyM">{action.label}</Text>
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
}
